/**
 * The booking state after a turn, compared with the state before it (the old kiosk's panel,
 * backend/frontend/src/components/shared/turnState.ts, docs/NEW-UI/07 D-09).
 *
 * The backend sends `debug_state: {before, after}` with every reply: each a photo of the session taken by the domain
 * (`installed().snapshot`), `fsm` and `booking` always, `defaults` (what the kiosk would fill on its own: the model
 * sees it, the kiosk has not recorded it) and the count of the items on screen, where the domain has them.
 * Nested slots are compared leaf by leaf (`passengers.adults`), so the panel shows exactly what moved. Pure.
 */
import { z } from 'zod';
import type { AssistantMessage } from '../core/wire';

const snapshotSchema = z.object({
  fsm: z.string(),
  booking: z.record(z.string(), z.unknown()).default({}),
  defaults: z.record(z.string(), z.unknown()).default({}),
  results: z.number().optional(),
});
const turnStateSchema = z.object({ before: snapshotSchema, after: snapshotSchema });

export type Snapshot = z.infer<typeof snapshotSchema>;
export type TurnState = z.infer<typeof turnStateSchema>;

/** The rows that are not slots: the screen and how many items are on it. The view names them. */
export const FSM_ROW = 'fsm';
export const RESULTS_ROW = 'on_screen';

/**
 * `earlier`: a slot the kiosk holds that this turn did not touch (the booking starts empty, so an earlier turn set it).
 * `same`: the screen or the items on it, unchanged (they are never "set").
 * `adopted`: a default the kiosk has now recorded with the same value — not a change the traveller would see.
 */
export type RowStatus = 'added' | 'changed' | 'removed' | 'earlier' | 'same' | 'default' | 'adopted';

export interface StateRow {
  key: string;
  value: string;
  previous?: string;
  status: RowStatus;
}

/** The turn's state, or null when the reply has none (an older recording) or it is not a snapshot. */
export function turnStateOf(message: AssistantMessage): TurnState | null {
  const parsed = turnStateSchema.safeParse(message.debug_state);
  return parsed.success ? parsed.data : null;
}

function display(value: unknown): string {
  if (Array.isArray(value)) return value.map((v) => (typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v))).join(', ');
  return String(value);
}

/** Leaf values keyed by their dotted path, in the order the backend sent them. */
export function flatten(obj: Record<string, unknown> | undefined, prefix = ''): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj ?? {})) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v !== null && typeof v === 'object' && !Array.isArray(v)) Object.assign(out, flatten(v as Record<string, unknown>, key));
    else out[key] = display(v);
  }
  return out;
}

function compared(key: string, before: string | undefined, after: string, unchanged: RowStatus): StateRow {
  if (before === undefined) return { key, value: after, status: 'added' };
  if (before !== after) return { key, value: after, previous: before, status: 'changed' };
  return { key, value: after, status: unchanged };
}

/** One row per slot: the screen, what the kiosk holds, what it would fill, the items on screen. */
export function stateRows({ before, after }: TurnState): StateRow[] {
  const was = flatten(before.booking);
  const wasDefault = flatten(before.defaults);
  const now = flatten(after.booking);
  const defaults = flatten(after.defaults);

  const rows: StateRow[] = [compared(FSM_ROW, before.fsm, after.fsm, 'same')];
  for (const [key, value] of Object.entries(now)) {
    if (!(key in was) && key in wasDefault) {
      // a default the kiosk recorded this turn: a change only if the value moved
      rows.push(wasDefault[key] === value ? { key, value, status: 'adopted' } : { key, value, previous: wasDefault[key], status: 'changed' });
    } else {
      rows.push(compared(key, was[key], value, 'earlier'));
    }
  }
  for (const [key, value] of Object.entries(was)) {
    if (!(key in now)) rows.push({ key, value: defaults[key] ?? '', previous: value, status: 'removed' });
  }
  for (const [key, value] of Object.entries(defaults)) {
    if (!(key in now) && !(key in was)) rows.push({ key, value, status: 'default' });
  }
  if (before.results || after.results) rows.push(compared(RESULTS_ROW, String(before.results ?? 0), String(after.results ?? 0), 'same'));
  return rows;
}

const MOVED: ReadonlySet<RowStatus> = new Set(['added', 'changed', 'removed']);

/** How many slots this turn touched: the chip shows it. */
export function changedCount(state: TurnState): number {
  return stateRows(state).filter((r) => MOVED.has(r.status)).length;
}
